import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { Button, FormCard, FormField, Skeleton } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import {
  type ParametresFiscauxDTO,
  getParametresFiscaux,
  updateParametresFiscaux,
} from '@/services/parametres';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';

interface FormValues {
  tva_standard: number;
  tva_reduite: number;
  tap: number;
  cnas_salarie: number;
  cnas_patronal: number;
  iep_taux_par_an: number;
  iep_plafond_pct: number;
  irg_plafond_exoneration: number;
  seuil_paiement_especes_b2b: number;
}

const PCT_FIELDS: (keyof FormValues)[] = [
  'tva_standard',
  'tva_reduite',
  'tap',
  'cnas_salarie',
  'cnas_patronal',
  'iep_taux_par_an',
  'iep_plafond_pct',
];

export function ParametresFiscauxTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['parametres-fiscaux'], queryFn: getParametresFiscaux });
  const { register, handleSubmit, reset } = useForm<FormValues>();

  useEffect(() => {
    if (data) {
      reset({
        tva_standard: data.tva_standard * 100,
        tva_reduite: data.tva_reduite * 100,
        tap: data.tap * 100,
        cnas_salarie: data.cnas_salarie * 100,
        cnas_patronal: data.cnas_patronal * 100,
        iep_taux_par_an: data.iep_taux_par_an * 100,
        iep_plafond_pct: data.iep_plafond_pct * 100,
        irg_plafond_exoneration: data.irg_plafond_exoneration,
        seuil_paiement_especes_b2b: data.seuil_paiement_especes_b2b,
      });
    }
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload: Partial<ParametresFiscauxDTO> = { ...values };
      // Reconvertit les pourcentages en fractions.
      for (const f of PCT_FIELDS) payload[f] = Number(values[f]) / 100;
      return updateParametresFiscaux(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parametres-fiscaux'] });
      toast.success('Paramètres fiscaux enregistrés.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  if (isLoading || !data) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-6">
      <FormCard
        title="Taux légaux"
        description="Taux applicables aux calculs de TVA, paie et fiscalité. Exprimés en pourcentage."
        columns={3}
        footer={
          <Button type="submit" loading={mutation.isPending}>
            <Save /> Enregistrer
          </Button>
        }
      >
        <FormField label="TVA standard (%)">
          <Input type="number" step="any" {...register('tva_standard')} />
        </FormField>
        <FormField label="TVA réduite (%)">
          <Input type="number" step="any" {...register('tva_reduite')} />
        </FormField>
        <FormField label="TAP (%)">
          <Input type="number" step="any" {...register('tap')} />
        </FormField>
        <FormField label="CNAS salarié (%)">
          <Input type="number" step="any" {...register('cnas_salarie')} />
        </FormField>
        <FormField label="CNAS patronal (%)">
          <Input type="number" step="any" {...register('cnas_patronal')} />
        </FormField>
        <FormField label="IEP taux/an (%)">
          <Input type="number" step="any" {...register('iep_taux_par_an')} />
        </FormField>
        <FormField label="IEP plafond (%)">
          <Input type="number" step="any" {...register('iep_plafond_pct')} />
        </FormField>
        <FormField label="Plafond exonération IRG (DZD)">
          <Input type="number" step="1" {...register('irg_plafond_exoneration')} />
        </FormField>
        <FormField label="Seuil paiement espèces B2B (DZD)">
          <Input type="number" step="1" {...register('seuil_paiement_especes_b2b')} />
        </FormField>
      </FormCard>

      <FormCard title="Barème IRG (salaires)" description="Tranches progressives mensuelles (lecture seule pour l'instant).">
        <div className="sm:col-span-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-2 font-semibold text-content-muted">Tranche</th>
                <th className="py-2 text-right font-semibold text-content-muted">Taux</th>
              </tr>
            </thead>
            <tbody>
              {data.bareme_irg.map((t, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  <td className="py-2 tabular-nums">
                    {formatDZD(t.min, { decimals: 0 })} — {t.max != null ? formatDZD(t.max, { decimals: 0 }) : '∞'}
                  </td>
                  <td className="py-2 text-right font-semibold tabular-nums">{(t.taux * 100).toFixed(0)} %</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </FormCard>
    </form>
  );
}
